import React, { useEffect, useState, useMemo, useContext } from 'react';
import {
  Table,
  Button,
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit';
import useTablePaginationAndSort from 'hooks/useTablePaginationAndSort';
import tableSort from 'helpers/tableSort';
import { Context } from 'components/Store';
import LoadingSpinner from 'components/LoadingSpinner';
import MapIcon from 'components/Icons/MapIcon';
import useTableScrollConfig from 'screens/Applications&WorkOrders/Components/DetailsTabs/hooks/useTableScrollConfig';
import { OPERATION_DETAILS_TABS_WRAPPER_SELECTOR } from 'screens/Applications&WorkOrders/Components/DetailsTabs/constants';
import { OperationPropertyVO } from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import ViewOnMapDrawer from './components/ViewOnMapDrawer';
import getAppliedAreaTableColumns from './utilities/getAppliedAreaTableColumns';
import { PAGE_SIZE } from './constants';
import './index.css';

const APPLIED_AREA_TABLE_SCROLL_X = 723;
const APPLIED_AREA_TABLE_DESKTOP_OFFSET_EM = 42;
const APPLIED_AREA_TABLE_LEADING_CONTENT_HEIGHT = 40;

interface AppliedAreaTabProps {
  propertiesData: OperationPropertyVO[];
  cropSeasonIds: string[];
  isLoading?: boolean;
}

const AppliedAreaTab = ({
  propertiesData,
  cropSeasonIds,
  isLoading = false
}: AppliedAreaTabProps) => {
  const [{ cropSeasons }] = useContext(Context) as any;

  const {
    reactUiKitTablePaginationProp,
    reactUiKitTableOnChange,
    setTotalItemsCount,
    sortBy,
    sortDir,
    page
  } = useTablePaginationAndSort({
    pageSize: PAGE_SIZE,
    defaultSortBy: 'farmName',
    defaultSortDir: 'ascend'
  }) as any;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Extract crop season names from cropSeasonIds
  const cropSeasonNames = useMemo(() => {
    if (!cropSeasonIds.length || !cropSeasons || cropSeasons.length === 0)
      return '';

    return cropSeasonIds
      .map((id: string) => {
        const season = cropSeasons.find((cs: any) => cs.id === id);
        return season ? season.name : null;
      })
      .filter(Boolean)
      .join(' | ');
  }, [cropSeasonIds, cropSeasons]);

  // Apply sorting to the data with custom logic for cropZoneName and farmName columns
  const sortedData = useMemo(() => {
    // Special handling for farmName column - sort by farmName, then fieldName, then cropZoneName
    if (sortBy === 'farmName') {
      const direction = sortDir === 'ascend' ? 1 : -1;
      return [...propertiesData].sort((a, b) => {
        // Primary sort: farmName
        const farmCompare =
          (a.farmName || '')
            .toLowerCase()
            .localeCompare((b.farmName || '').toLowerCase()) * direction;

        if (farmCompare !== 0) return farmCompare;

        // Secondary sort: fieldName
        const fieldCompare =
          (a.fieldName || '')
            .toLowerCase()
            .localeCompare((b.fieldName || '').toLowerCase()) * direction;

        if (fieldCompare !== 0) return fieldCompare;

        // Tertiary sort: cropZoneName
        return (
          (a.cropZoneName || '')
            .toLowerCase()
            .localeCompare((b.cropZoneName || '').toLowerCase()) * direction
        );
      });
    }

    // Special handling for cropZoneName column - sort by fieldName first, then cropZoneName
    if (sortBy === 'cropZoneName') {
      const direction = sortDir === 'ascend' ? 1 : -1;
      return [...propertiesData].sort((a, b) => {
        // Primary sort: fieldName
        const fieldCompare =
          (a.fieldName || '')
            .toLowerCase()
            .localeCompare((b.fieldName || '').toLowerCase()) * direction;

        if (fieldCompare !== 0) return fieldCompare;

        // Secondary sort: cropZoneName
        return (
          (a.cropZoneName || '')
            .toLowerCase()
            .localeCompare((b.cropZoneName || '').toLowerCase()) * direction
        );
      });
    }

    // Default sorting for other columns
    return tableSort(propertiesData, sortBy, sortDir);
  }, [propertiesData, sortBy, sortDir]);

  // Apply pagination to the sorted data
  const paginatedData = useMemo(() => {
    return sortedData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [sortedData, page]);

  // Initialize pagination with total count
  useEffect(() => {
    setTotalItemsCount(sortedData.length);
  }, [sortedData.length, setTotalItemsCount]);

  const tableScrollConfig = useTableScrollConfig({
    x: APPLIED_AREA_TABLE_SCROLL_X,
    rowCount: paginatedData.length,
    desktopOffsetEm: APPLIED_AREA_TABLE_DESKTOP_OFFSET_EM,
    leadingHeight: APPLIED_AREA_TABLE_LEADING_CONTENT_HEIGHT,
    wrapperSelector: OPERATION_DETAILS_TABS_WRAPPER_SELECTOR
  });

  // Get table columns
  const columns = useMemo(
    () =>
      getAppliedAreaTableColumns({
        sortedDataLength: sortedData.length,
        sortBy,
        sortDir
      }),
    [sortedData.length, sortBy, sortDir]
  );

  const handleViewOnMap = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  if (isLoading) {
    return (
      <div
        data-testid="applied-area-tab"
        style={{ height: 'calc(100vh - 33em)' }}
        className="flex items-center justify-center"
      >
        <LoadingSpinner
          message=""
          className="h-full applied-area-tab-loading-spinner"
        />
      </div>
    );
  }

  return (
    <div
      data-testid="applied-area-tab"
      style={{ padding: '24px 24px 32px 24px' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '8px'
        }}
      >
        <Button
          type={ButtonType.primary}
          ghost
          size="large"
          onClick={handleViewOnMap}
          style={{ width: '130px', height: '32px', padding: '4px 12px' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '14px'
            }}
          >
            <MapIcon color="#14803C" size={16} />
            <span>View on Map</span>
          </div>
        </Button>
      </div>
      <div
        className={`applied-area-table ${
          sortedData.length <= PAGE_SIZE ? 'hide-pagination-nav' : ''
        }`}
      >
        <Table
          rowKey="cropZoneId"
          dataSource={paginatedData}
          columns={columns}
          scroll={tableScrollConfig}
          pagination={reactUiKitTablePaginationProp}
          onChange={reactUiKitTableOnChange}
          tableLayout="auto"
        />
      </div>

      <ViewOnMapDrawer
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        properties={sortedData}
        cropSeasonNames={cropSeasonNames}
      />
    </div>
  );
};

export default AppliedAreaTab;
